import {
  ChoiceGroup,
  IChoiceGroupOption,
  mergeStyleSets,
} from "@fluentui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import ErrorMsg from "./ErrorMsg";
import { FieldContainer } from "./FieldContainer";

enum EChoiceKey {
  true = "true",
  false = "false",
  null = "null",
}

const getChoiceGroupStyles = (
  theme: IStyledTheme,
  isMarked: boolean,
  verticalView: boolean
) => ({
  flexContainer: {
    display: "flex",
    flexDirection: verticalView ? "column" : "row",
    gap: verticalView ? 0 : 15,
    ...(isMarked && {
      background: theme.palette.yellowLight,
      padding: "8px",
      borderRadius: "4px",
    }),
  },
  ...(isMarked && {
    choiceFieldWrapper: {
      ".ms-ChoiceField-field": {
        color: theme.isDark ? "black" : "inherit",
      },
      ".ms-ChoiceField-labelWrapper": {
        color: theme.isDark ? "black" : "inherit",
      },
    },
  }),
});

interface RadioGroupFieldProps {
  disabled?: boolean;
  label: string;
  name: string;
  setFieldValue: (name: string, value: any) => void;
  verticalView?: boolean;
  checked?: boolean | null;
  errors: Record<string, string>;
  isMarked?: boolean;
  hideNotDefined?: boolean;
  [key: string]: any;
}

const RadioGroupField = ({
  disabled,
  label,
  name,
  setFieldValue,
  verticalView = false,
  checked,
  errors,
  isMarked = false,
  hideNotDefined = false,
  ...props
}: RadioGroupFieldProps) => {
  const { t } = useTranslation();
  const theme = useTheme() as IStyledTheme;

  const options: IChoiceGroupOption[] = [
    { key: EChoiceKey.true, text: t("greco.yes") },
    { key: EChoiceKey.false, text: t("greco.no") },
    ...(hideNotDefined
      ? []
      : [{ key: EChoiceKey.null, text: t("greco.notDefined") }]),
  ];

  const selectedKey =
    options.find((item) => {
      if (checked === null) {
        return item.key === EChoiceKey.null;
      }
      return item.key === checked?.toString();
    })?.key || EChoiceKey.null;

  const choiceGroupStyles = getChoiceGroupStyles(theme, isMarked, verticalView);

  return (
    <div className={classes.wrap}>
      <FieldContainer isTooltipHidden={disabled} tooltipText={label}>
        <ChoiceGroup
          id={name + "-choicegroup"}
          selectedKey={selectedKey}
          styles={choiceGroupStyles}
          options={options}
          onChange={(e, value) => {
            setFieldValue(name, value.key);
          }}
          label={t(label)}
          disabled={disabled}
          {...props}
        />
        {errors[name] && <ErrorMsg>{errors[name]}</ErrorMsg>}
      </FieldContainer>
    </div>
  );
};

export default React.memo(RadioGroupField);

const classes = mergeStyleSets({
  wrap: {
    height: "100%",
    display: "flex",
    alignItems: "center",
  },
});
